import React from 'react';
import { Link } from 'react-router-dom'; // ✅ This import is required here too
import { Code2 } from "lucide-react";
import SEO from '../components/SEO';
const themeColors = {
  blue: "bg-[#0a3c6d]",
  orange: "bg-[#ff9b00]",
  green: "bg-[#8cc540]",
};

const title="Our Services | AKHSoft";
const description = "Discover AKHSoft's full range of digital services, including custom software development, web design, and digital marketing.";
const keywords = "AKHSoft services, custom software, website development, digital marketing, ERP, mobile apps";
const path = typeof window !== "undefined" ? window.location.pathname : "";
// ServiceBox Component
const ServiceBox = ({ icon, title, desc, link, theme }) => {
  const bgColor = themeColors[theme] || "bg-[#0a3c6d]";

  return (
    <div className="bg-white shadow-lg rounded-2xl p-6 text-center hover:shadow-xl transition duration-300">
      <div className={`${bgColor} w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4`}>
        <span className="text-3xl text-white">{icon}</span> {/* ← White icon here */}
      </div>
      <h3 className="text-xl font-bold mb-2 text-gray-800">{title}</h3>
      <p className="text-gray-600 mb-4">{desc}</p>
      <Link to={link} className="text-sm font-medium text-[#0a3c6d] hover:underline">
        Learn More →
      </Link>
    </div>
  );
};

// Services Page
export default function Services() {
  return (
    <>
    <SEO 
      title={title}
      description={description}
      keywords={keywords}
      path={path}
    />
    <div className="max-w-7xl mx-auto px-4 py-12 space-y-16">
      <div className="text-center py-10 animate-fade-in">
  <h1 className="text-4xl md:text-5xl font-extrabold text-[#0a3c6d] mb-2 text-center">
    Our Services
    <span className="w-24 h-1 bg-[#0a3c6d] mx-auto -mt-2 mb-4 rounded-full"></span>
  </h1>
  <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
    We provide innovative digital solutions tailored to your business needs—from software engineering to website design and marketing.
  </p>
</div>
      {/* Separator Line */}
  <div className="block w-24 h-1 bg-[#0a3c6d] mx-auto -mt-2 mb-4 rounded-full"></div>

      {/* Software Development */}
      <section>
        <div className="text-center animate-fade-in">
  <h2 className="text-3xl font-extrabold text-[#0a3c6d] mb-2 relative inline-block">
    Software Development
    <span className="block h-1 w-16 bg-[#0a3c6d] mx-auto mt-2 rounded-full"></span>
  </h2>
  <p className="text-gray-700 mb-6 max-w-2xl mx-auto text-lg leading-relaxed">
    From powerful custom software to seamless enterprise apps, our software engineers build high-performance solutions for your business.
  </p>
</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <ServiceBox
            icon="💻"
            title="Custom Software Development"
            desc="Turn your ideas into scalable custom applications."
            link="/software-development/custom-software-development"
            theme="blue"
          />
          <ServiceBox
            icon="📱"
            title="Enterprise Mobile Applications"
            desc="Secure, scalable mobile solutions tailored for business workflows."
            link="/software-development/enterprise-mobile-applications"
            theme="blue"
          />
          <ServiceBox
            icon="🤝"
            title="Online Collaboration Platforms"
            desc="Boost team productivity with cloud-based collaboration platforms."
            link="/software-development/online-collaboration-platforms"
            theme="blue"
          />
          <ServiceBox
            icon="🎓"
            title="Digital Learning Solutions"
            desc="Empower learners with scalable and interactive e-learning platforms."
            link="/software-development/digital-learning-solutions"
            theme="blue"
          />
        </div>
      </section>

      {/* Website Design & Development */}
      <section>
        <div className="text-center animate-fade-in">
  <h2 className="text-3xl font-extrabold text-[#ff9b00] mb-2 relative inline-block">
    Website Design & Development
    <span className="block h-1 w-16 bg-[#ff9b00] mx-auto mt-2 rounded-full"></span>
  </h2>
  <p className="text-gray-700 mb-6 max-w-2xl mx-auto text-lg leading-relaxed">
    We create beautiful, responsive websites that reflect your brand and drive engagement across all devices.
  </p>
</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <ServiceBox
            icon="🌐"
            title="WordPress Website Design"
            desc="Modern, customizable websites using WordPress CMS."
            link="/website-design/wordpress-website-development"
            theme="orange"
          />
          <ServiceBox
            icon="🏢"
            title="Corporate Website Design"
            desc="Enterprise-grade websites with custom CMS integration."
            link="/website-design/corporate-website-development"
            theme="orange"
          />
          <ServiceBox
            icon="🛒"
            title="eCommerce Website Design"
            desc="Launch powerful online stores using platforms like Magento or Shopify."
            link="/website-design/ecommerce-website-development"
            theme="orange"
          />
          <ServiceBox
            icon="🧰"
            title="Website Hosting & Support"
            desc="Reliable hosting, maintenance, and technical support."
            link="/website-design/website-hosting-support"
            theme="orange"
          />
        </div>
      </section>

      {/* Digital Marketing */}
      <section>
        <div className="text-center animate-fade-in">
  <h2 className="text-3xl font-extrabold text-[#8cc540] mb-2 relative inline-block">
    Digital Marketing
    <span className="block h-1 w-16 bg-[#8cc540] mx-auto mt-2 rounded-full"></span>
  </h2>
  <p className="text-gray-700 mb-6 max-w-2xl mx-auto text-lg leading-relaxed">
    Drive visibility, engagement, and sales through SEO, PPC, content, and social media strategies tailored to your goals.
  </p>
</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <ServiceBox
            icon="🔍"
            title="Search Engine Optimisation (SEO)"
            desc="Improve your visibility and rank higher in organic search results."
            link="/digital-marketing/search-engine-optimisation-seo"
            theme="green"
          />
          <ServiceBox
            icon="📈"
            title="Search Engine Marketing (SEM)"
            desc="Maximize your return on investment with targeted PPC campaigns."
            link="/digital-marketing/search-engine-marketing-sem"
            theme="green"
          />
          <ServiceBox
            icon="✍️"
            title="Content Marketing"
            desc="Deliver value and drive engagement with high-quality content."
            link="/digital-marketing/content-marketing"
            theme="green"
          />
          <ServiceBox
            icon="📣"
            title="Social Media Marketing"
            desc="Connect and engage with your audience on all major platforms."
            link="/digital-marketing/social-media-marketing-smm"
            theme="green"
          />
        </div>
      </section>
    </div>
    </>
  );
}